import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import ConcertCard from "@/components/ConcertCard";
import FAQ from "@/components/FAQ";
import { getConcerts, formatShortDateFr } from "@/lib/api";

const VENUES = [
  { name: "Paris La Défense Arena", city: "Nanterre", capacity: "40 000 places", note: "La plus grande salle d'Europe" },
  { name: "Accor Arena", city: "Paris Bercy", capacity: "20 300 places", note: "L'iconique salle parisienne" },
];

export default function Home() {
  const [concerts, setConcerts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getConcerts();
        setConcerts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const featured = concerts.find((c) => c.slug === "celine-dion");
  const rest = concerts.filter((c) => c.slug !== "celine-dion");
  const filtered = filter === "all" ? rest : rest.filter((c) => c.venue === filter);

  return (
    <>
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden grain" data-testid="hero-section">
        {featured && (
          <img
            src={featured.image_url}
            alt={featured.artist}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 md:pt-40 pb-16 min-h-screen flex flex-col justify-end">
          <p className="overline rise" data-testid="hero-overline">
            Billetterie officielle · Paris · 2026
          </p>
          <h1
            className="mt-4 text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] rise-delay-1"
            data-testid="hero-title"
          >
            L'émotion live,<br />
            <span className="text-primary">en direct de Paris.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 rise-delay-2">
            Céline Dion, Drake, Iron Maiden, Damso, Olivia Rodrigo… Vivez les plus grands concerts
            à Paris La Défense Arena et à l'Accor Arena. Billets officiels, paiement sécurisé.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 rise-delay-3">
            {featured && (
              <Link
                to={`/concert/${featured.slug}`}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-4 rounded-sm font-bold text-base hover:brightness-110 transition"
                data-testid="hero-cta-celine-btn"
              >
                <Sparkles size={18} />
                Réserver Céline Dion
                <ArrowRight size={18} />
              </Link>
            )}
            <a
              href="#concerts"
              className="inline-flex items-center gap-2 border border-white/30 px-6 py-4 rounded-sm font-bold text-base hover:bg-white hover:text-black transition"
              data-testid="hero-cta-all-btn"
            >
              Voir tous les concerts
            </a>
          </div>

          {featured && (
            <div className="mt-12 flex flex-wrap gap-3 text-xs font-mono text-white/70 max-w-3xl">
              {featured.dates.slice(0, 10).map((d) => (
                <span
                  key={d}
                  className="border border-white/20 bg-black/40 backdrop-blur px-3 py-1.5 rounded-sm"
                >
                  {formatShortDateFr(d)}
                </span>
              ))}
              {featured.dates.length > 10 && (
                <span className="border border-primary/40 bg-primary/10 text-primary px-3 py-1.5 rounded-sm">
                  +{featured.dates.length - 10} dates
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      <Marquee />

      {/* CONCERTS */}
      <section id="concerts" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="concerts-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline">Tous les concerts</p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mt-3">
              Le programme<span className="text-primary">.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "all", label: "Tous" },
              { k: "Paris La Défense Arena", label: "La Défense Arena" },
              { k: "Accor Arena", label: "Accor Arena" },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setFilter(opt.k)}
                className={`font-mono text-xs tracking-wider uppercase px-4 py-2 rounded-sm border transition ${
                  filter === opt.k
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-white/20 text-muted-foreground hover:text-foreground hover:border-white/40"
                }`}
                data-testid={`filter-${opt.k.replace(/\s+/g, "-").toLowerCase()}-btn`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-card border border-white/10 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {filter === "all" && featured && <ConcertCard concert={featured} large />}
            {filtered.map((c) => (
              <ConcertCard key={c.slug} concert={c} />
            ))}
          </div>
        )}
      </section>

      {/* VENUES */}
      <section id="venues" className="bg-black/60 border-y border-white/10" data-testid="venues-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <p className="overline">Les salles</p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mt-3 mb-12">
            Deux temples<br />du live à Paris<span className="text-primary">.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {VENUES.map((v) => (
              <div
                key={v.name}
                className="border border-white/10 bg-card p-8 md:p-10 rounded-sm hover:border-white/30 transition"
                data-testid={`venue-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center gap-2 text-primary">
                  <MapPin size={16} />
                  <span className="font-mono text-xs tracking-[0.2em] uppercase">{v.city}</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-black tracking-tight mt-3">{v.name}</h3>
                <p className="font-mono text-xs text-muted-foreground mt-2">{v.capacity}</p>
                <p className="text-base text-muted-foreground mt-6 leading-relaxed">{v.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQ />

      <Footer />
    </>
  );
}
